import type { AxiosRequestConfig } from 'axios'
import merge from 'lodash.merge'

import type { UserApiKeyId, UserId } from '@beaded/models'

import type { SignupProps } from 'forms/Signup'

import type { Obj, ID } from 'types/common'

import * as apiUtils from '.'

const url = '/user'

export const getSelf = (options: AxiosRequestConfig = {}) =>
  apiUtils.GET(`${url}/me`, options)

export const get = (id: UserId, options: AxiosRequestConfig = {}) =>
  apiUtils.GET(`${url}/${id}`, options)

export const getAll = (options: AxiosRequestConfig = {}) =>
  apiUtils.GET(`${url}/`, options)

interface IUserUpdateDoc {
  password?: string
  org?: { id: number }
  prefs?: Record<string, unknown>
  name?: { first: string; last: string }
  email?: string
  apiKey?: boolean
}
export const updateUser = (userId: UserId, updateDoc: IUserUpdateDoc) =>
  apiUtils.PUT(`${url}/${userId}`, { ...updateDoc })

export const updatePassword = (userId: UserId, password: string) =>
  updateUser(userId, { password })

export const triggerReset = (userId: UserId) =>
  apiUtils.POST(`${url}/${userId}/trigger-reset`, {})

export const updatePrefs = (userId: UserId, prefs: IUserUpdateDoc['prefs']) =>
  updateUser(userId, { prefs })

export const updateName = (userId: UserId, name: IUserUpdateDoc['name']) =>
  updateUser(userId, { name })

export const addOrg = (userId: UserId, org: IUserUpdateDoc['org']) =>
  updateUser(userId, { org })

export const removeOrg = (userId: UserId, org: IUserUpdateDoc['org']) =>
  apiUtils.DELETE(`${url}/`, undefined, { params: { userId, org } })

export const addApiKey = (userId: UserId) =>
  updateUser(userId, { apiKey: true })

// DELETE input is read from the query string (express-zod-api ignores a
// DELETE body), so the key travels as a param -- by its non-secret reference
// (apiKeys[].ref), never its value: query strings are logged (#4411).
export const removeApiKey = (userId: UserId, apiKeyRef: string) =>
  apiUtils.DELETE(`${url}/`, undefined, { params: { userId, apiKeyRef } })

// Self-service API key management (issue #3687). These hit the session-scoped
// endpoints in apps/api/src/ez/endpoints/userApiKey.ts -- no userId is sent;
// the server derives the user from the session cookie.
export interface CreateApiKeyInput {
  type: 'MCP'
  organizationId?: number | null
  isReadOnly?: boolean
  rateLimitPerMinute?: number
}

export const createApiKey = (
  data: CreateApiKeyInput,
  options: AxiosRequestConfig = {},
) => apiUtils.POST(`${url}/api-key`, { ...data }, options)

export const listApiKeys = (options: AxiosRequestConfig = {}) =>
  apiUtils.GET(`${url}/api-keys`, options)

export const revokeApiKey = (
  keyId: UserApiKeyId,
  options: AxiosRequestConfig = {},
) => apiUtils.DELETE(`${url}/api-key/${keyId}`, undefined, options)

export const remove = (id: ID) => apiUtils.DELETE(`${url}/${id}`)

// save user settings
export const create = (data: Obj) => apiUtils.POST(`${url}/`, data)

export const login = (creds: {
  username: string
  password: string
  rememberMe?: boolean
}) => apiUtils.POST(`${url}/session`, creds)

export const logout = () => apiUtils.DELETE(`${url}/session`)

export const signup = (data: SignupProps) =>
  apiUtils.POST(`${url}/signup`, data)

export const getMenuProjection = (options: AxiosRequestConfig = {}) =>
  apiUtils.GET(`${url}/`, merge({ params: { projection: 'menu' } }, options))

export const requestPasswordReset = (username: string) =>
  apiUtils.POST(`${url}/reset/`, { email: username })

export const authenticateOTP = (otp: string) =>
  apiUtils.GET(`${url}/once/${otp}`)

export const resetPassword = (otp: string, password: string) =>
  apiUtils.PUT(`${url}/reset/`, { otp, password })

export const requestMagicLink = (email: string, rememberMe = false) =>
  apiUtils.POST(`${url}/magic-link`, { email, rememberMe })

export const redeemMagicLink = (token: string) =>
  apiUtils.POST(`${url}/magic-link/redeem`, { token })

export const addToOrg = (data: {
  nameFirst: string
  nameLast: string
  email: string
  org: { id: ID }
}) => apiUtils.POST(`${url}/add-to-org`, data)

export const removeFromOrg = (data: { email: string; org: { id: ID } }) =>
  apiUtils.POST(`${url}/remove-from-org`, data)

export const addToProject = (data: {
  nameFirst: string
  nameLast: string
  email: string
  project: { id: ID }
}) => apiUtils.POST(`${url}/add-to-project`, data)

export const removeFromProject = (data: {
  email: string
  project: { id: ID }
}) => apiUtils.POST(`${url}/remove-from-project`, data)

export const patch = (id: UserId, data: Obj) =>
  apiUtils.PATCH(`${url}/${id}`, data)

export const startViewAs = (email: string, options: AxiosRequestConfig = {}) =>
  apiUtils.POST(`${url}/view-as`, { email }, options)

export const endViewAs = (options: AxiosRequestConfig = {}) =>
  apiUtils.DELETE(`${url}/view-as`, undefined, options)

export const switchToUser = (email: string, options: AxiosRequestConfig = {}) =>
  apiUtils.POST(`${url}/switch`, { email }, options)
